import { zodResolver } from "@hookform/resolvers/zod";
import { AxiosError } from "axios";
import { KeyRound } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";

import { SSO_LOGIN_URL } from "@/api/auth";
import { ssoErrorText } from "@/auth/sso-messages";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/auth/useAuth";
import { extractErrorMessage } from "@/lib/api-errors";
import { usePortalConfig } from "@/lib/use-portal-config";

const schema = z.object({
  username: z.string().min(1, "Введите логин"),
  password: z.string().min(1, "Введите пароль"),
});

type FormValues = z.infer<typeof schema>;

export function LoginPage(): React.ReactElement {
  const { user, login, loading, restoreError } = useAuth();
  const portalConfig = usePortalConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [serverError, setServerError] = useState<string | null>(null);

  // Возврат из SSO: бэкенд уводит сюда с `?sso=ok` или `?sso_error=<код>`.
  // На `sso=ok` refresh-cookie уже стоит, и сессию поднимает ОБЫЧНОЕ
  // восстановление при загрузке (`AuthContext` → `/refresh`). Второй
  // `/refresh` отсюда не делаем: два параллельных обмена одной cookie —
  // это повтор отозванного токена, и бэкенд отзывает ВСЕ сессии человека.
  // Вошли → `<Navigate>` ниже уводит дальше; не вошли → показываем причину.
  // Параметр из адреса убираем, чтобы перезагрузка не повторяла сообщение.
  const ssoResult = searchParams.get("sso");
  const ssoErrorCode = searchParams.get("sso_error");
  useEffect(() => {
    if (ssoResult === null && ssoErrorCode === null) return;
    if (loading || user) return;
    if (ssoErrorCode !== null) {
      setServerError(ssoErrorText(ssoErrorCode));
    } else if (ssoResult === "ok") {
      setServerError(
        `Вход через SSO не завершён: ${extractErrorMessage(
          restoreError,
          "сессия портала не получена",
        )}. Попробуйте ещё раз.`,
      );
    } else {
      setServerError(`Вход через SSO: неизвестный ответ «${ssoResult}».`);
    }
    const next = new window.URLSearchParams(searchParams);
    next.delete("sso");
    next.delete("sso_error");
    setSearchParams(next, { replace: true, state: location.state });
  }, [
    ssoResult,
    ssoErrorCode,
    loading,
    user,
    restoreError,
    searchParams,
    setSearchParams,
    location.state,
  ]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  if (loading) return <div className="p-8 text-center">Загрузка…</div>;
  if (user) {
    const redirectTo = (location.state as { from?: string } | null)?.from ?? "/";
    return <Navigate to={redirectTo} replace />;
  }

  const onSubmit = async (values: FormValues) => {
    setServerError(null);
    try {
      await login(values.username, values.password);
      const redirectTo = (location.state as { from?: string } | null)?.from ?? "/";
      navigate(redirectTo, { replace: true });
    } catch (err) {
      if (err instanceof AxiosError) {
        const detail = err.response?.data?.detail;
        setServerError(
          err.response?.status === 429
            ? "Слишком много попыток. Попробуйте позже."
            : typeof detail === "string"
              ? detail
              : "Ошибка входа",
        );
      } else {
        setServerError("Ошибка входа");
      }
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>Портал продавца</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">Логин</Label>
              <Input id="username" autoComplete="username" autoFocus {...register("username")} />
              {errors.username && (
                <p className="text-sm text-destructive">{errors.username.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Пароль</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                {...register("password")}
              />
              {errors.password && (
                <p className="text-sm text-destructive">{errors.password.message}</p>
              )}
            </div>
            {serverError && <p className="text-sm text-destructive">{serverError}</p>}
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? "Входим…" : "Войти"}
            </Button>
          </form>

          {/* Ссылка, а не fetch: вход — полная навигация браузера через
              Zitadel и обратно. */}
          {portalConfig.data?.sso_enabled && (
            <Button asChild variant="outline" className="mt-4 w-full">
              <a href={SSO_LOGIN_URL}>
                <KeyRound className="mr-2 size-4" />
                Войти через SSO
              </a>
            </Button>
          )}
          {/* Конфигурация не загрузилась — говорим вслух, а не прячем кнопку SSO
              молча, будто он выключен. Парольный вход при этом работает. */}
          {portalConfig.error && (
            <p className="mt-4 text-sm text-destructive">
              Не удалось получить конфигурацию портала (/api/config):{" "}
              {extractErrorMessage(portalConfig.error, "нет ответа")}. Вход через SSO недоступен.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
